import AddIcon from '@mui/icons-material/Add';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { FC, MouseEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { ShipmentOrderForm } from 'src/components/V2ShipmentOrderForm';
import { ConvenienceDispatchFixedRouteEntity } from 'src/entities/ConvenienceDispatchFixedRoute.entity';
import {
  CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY,
  CONVENIENCE_DISPATCH_FIXED_ROUTE_DETAIL_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchFixedRoutes.query';
import {
  useDeleteFixedRouteMutation,
  useRenameFixedRouteMutation,
} from 'src/hooks/useConvenienceDispatchMutation';

import DisplayMetricToggle, { DisplayMetric } from './DisplayMetricToggle';
import FixedRouteSaveDialog from './FixedRouteSaveDialog';
import FixedRouteSelectorDialog from './FixedRouteSelectorDialog';

// TODO(Phase 9): ImportContext は /import ページ専用のため、コンビニ配送配車ページからは
// インポート完了イベントをリアルタイム検知できない。
// 将来的には以下のいずれかで実装する:
//   1. インポート完了後に ImportContext.currentStep === 4 を参照できるよう共有 Context 化する
//   2. インポート完了後に URL パラメータや localStorage で完了フラグを渡す
//   3. ポーリングまたは WebSocket でインポート完了を検知し ImportCompletedDialog を表示する
// 現時点では「インポート」ボタンで /import ページへ遷移する最小実装とする。

type Props = {
  startOn: string;
  endOn: string;
  fixedRouteId: number | null;
  fixedRoutes: ConvenienceDispatchFixedRouteEntity[];
  isLoadingFixedRoutes: boolean;
  isApplying: boolean;
  onDateRangeChange: (start: string, end: string) => void;
  onFixedRouteSelect: (fixedRouteId: number) => void;
  onFixedRouteCreated: (newFixedRouteId: number) => void;
  onFixedRouteCleared: () => void;
  onApplyFixedRoute: (overrideId?: number) => void;
  displayMetrics: DisplayMetric[];
  onDisplayMetricsChange: (metrics: DisplayMetric[]) => void;
  onNavigate: (path: string) => void;
  visibleColumns: number;
  onVisibleColumnsChange: (v: number) => void;
  /** 固定ルート選択ダイアログ(Presenter 側で管理) */
  selectorDialogOpen: boolean;
  onSelectorDialogOpenChange: (open: boolean) => void;
  /** 自動配車ボタン */
  onAutoDispatch: () => void;
  isAutoDispatchDisabled: boolean;
  autoDispatchTooltip?: string;
};

const ConvenienceDispatchToolbar: FC<Props> = memo(
  ({
    startOn,
    endOn,
    fixedRouteId,
    fixedRoutes,
    isLoadingFixedRoutes,
    isApplying,
    onDateRangeChange,
    onFixedRouteSelect,
    onFixedRouteCreated,
    onFixedRouteCleared,
    onApplyFixedRoute,
    displayMetrics,
    onDisplayMetricsChange,
    onNavigate,
    visibleColumns,
    onVisibleColumnsChange,
    selectorDialogOpen,
    onSelectorDialogOpenChange,
    onAutoDispatch,
    isAutoDispatchDisabled,
    autoDispatchTooltip,
  }) => {
    // ---- DateRangePicker ----
    const { enqueueSnackbar } = useSnackbar();
    const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([
      startOn ? new Date(startOn) : null,
      endOn ? new Date(endOn) : null,
    ]);

    useEffect(() => {
      setSelectedRange([
        startOn ? new Date(startOn) : null,
        endOn ? new Date(endOn) : null,
      ]);
    }, [startOn, endOn]);

    const handleDateRange = useCallback(
      (range: DateRange<Date>) => {
        setSelectedRange((prev) => [range[0] ?? prev[0], range[1] ?? prev[1]]);
        const start = range[0];
        const end = range[1];
        if (!start || !end) return;
        const diffDays = differenceInDays(end, start);
        if (diffDays < 0) {
          enqueueSnackbar('終了日は開始日よりも過去に設定できません');
          return;
        }
        onDateRangeChange(
          format(start, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' }),
          format(end, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' })
        );
      },
      [enqueueSnackbar, onDateRangeChange]
    );

    // ---- 固定ルートメニュー ----
    const fixedRouteMenuAnchorRef = useRef<HTMLButtonElement>(null);
    const [fixedRouteMenuOpen, setFixedRouteMenuOpen] = useState(false);
    const [fixedRouteSaveDialogOpen, setFixedRouteSaveDialogOpen] = useState(false);
    const [saveDialogMode, setSaveDialogMode] = useState<'new' | 'overwrite'>('new');

    const handleFixedRouteMenuClose = () => setFixedRouteMenuOpen(false);

    const handleOpenSaveAsNew = () => {
      handleFixedRouteMenuClose();
      setSaveDialogMode('new');
      setFixedRouteSaveDialogOpen(true);
    };

    const handleOpenOverwriteSave = () => {
      handleFixedRouteMenuClose();
      setSaveDialogMode('overwrite');
      setFixedRouteSaveDialogOpen(true);
    };

    // ---- 固定ルート 呼び出し確認ダイアログ ----
    const [loadDialogOpen, setLoadDialogOpen] = useState(false);
    const [loadTarget, setLoadTarget] = useState<ConvenienceDispatchFixedRouteEntity | null>(null);

    const handlePickFixedRoute = (route: ConvenienceDispatchFixedRouteEntity) => {
      handleFixedRouteMenuClose();
      setLoadTarget(route);
      setLoadDialogOpen(true);
    };

    const handleLoadConfirm = () => {
      const target = loadTarget;
      setLoadDialogOpen(false);
      setLoadTarget(null);
      if (!target) return;
      // コース選択 + 反映を 1 クリックで。
      // onApplyFixedRoute には target.id を明示的に渡す
      // （直後の onFixedRouteSelect で state 更新されるが、同期的には反映されないため）
      onFixedRouteSelect(target.id);
      onApplyFixedRoute(target.id);
    };

    const handleRouteSelect = (id: number) => {
      onFixedRouteSelect(id);
      onSelectorDialogOpenChange(false);
    };

    const formatRegistrationDate = (iso: string) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    // ---- 案件作成（V3Plans と同じ ShipmentOrderForm ダイアログ） ----
    const [newShipmentDialogOpen, setNewShipmentDialogOpen] = useState(false);

    // ---- 固定ルート 名前変更 / 削除 ----
    const queryClient = useQueryClient();
    const renameMutation = useRenameFixedRouteMutation();
    const deleteMutation = useDeleteFixedRouteMutation();

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState<ConvenienceDispatchFixedRouteEntity | null>(null);
    const [renameValue, setRenameValue] = useState('');

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<ConvenienceDispatchFixedRouteEntity | null>(null);

    const handleRenameClick = (e: MouseEvent, route: ConvenienceDispatchFixedRouteEntity) => {
      e.stopPropagation();
      handleFixedRouteMenuClose();
      setRenameTarget(route);
      setRenameValue(route.name);
      setRenameDialogOpen(true);
    };

    const handleRenameConfirm = () => {
      const target = renameTarget;
      const trimmed = renameValue.trim();
      if (!target || !trimmed) return;
      renameMutation.mutate(
        { fixedRouteId: target.id, name: trimmed },
        {
          onSuccess: () => {
            enqueueSnackbar(`「${trimmed}」に名前を変更しました`, { variant: 'success' });
            void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY] });
            void queryClient.invalidateQueries({
              queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTE_DETAIL_QUERY_KEY, target.id],
            });
            setRenameDialogOpen(false);
            setRenameTarget(null);
          },
          onError: () => {
            enqueueSnackbar('名前の変更に失敗しました', { variant: 'error' });
          },
        }
      );
    };

    const handleDeleteClick = (e: MouseEvent, route: ConvenienceDispatchFixedRouteEntity) => {
      e.stopPropagation();
      handleFixedRouteMenuClose();
      setDeleteTarget(route);
      setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
      const target = deleteTarget;
      if (!target) return;
      deleteMutation.mutate(
        { fixedRouteId: target.id },
        {
          onSuccess: () => {
            enqueueSnackbar(`「${target.name}」を削除しました`, { variant: 'success' });
            void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY] });
            if (fixedRouteId === target.id) {
              onFixedRouteCleared();
            }
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
          },
          onError: () => {
            enqueueSnackbar('削除に失敗しました', { variant: 'error' });
          },
        }
      );
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* 日付 */}
        <DateRangePicker
          value={selectedRange}
          onChange={handleDateRange}
          slotProps={{ textField: { size: 'small', sx: { p: 0, m: 0 } } }}
        />

        {/* 勤務計画 (V3Plans の ActionsPresenter#shiftButtonMemo を踏襲) */}
        <Button
          variant="outlined"
          onClick={() => {
            if (!startOn) return;
            // 戻り先を convenience-dispatches に指示。Shifts/Presenter#closeButtonOnClick で分岐対応済み
            onNavigate(`/shifts/${startOn}/${endOn || startOn}/convenience-dispatches`);
          }}
          disabled={!startOn}
          sx={{ borderRadius: '5px', height: '35px', p: 1.5 }}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
            勤務計画
          </Typography>
        </Button>

        <Box sx={{ flex: 1 }} />

        {/* 右側ボタン群 */}
        <Stack direction="row" spacing={0.75} alignItems="center">

          {/* 固定ルート ▾ */}
          <Button
            ref={fixedRouteMenuAnchorRef}
            data-fixed-route-trigger
            size="small"
            startIcon={<AltRouteIcon sx={{ fontSize: 14 }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
            onClick={() => setFixedRouteMenuOpen(true)}
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
          >
            固定ルート
          </Button>
          <Popover
            anchorEl={fixedRouteMenuAnchorRef.current}
            open={fixedRouteMenuOpen}
            onClose={handleFixedRouteMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 320, maxHeight: '80vh', overflowY: 'auto' } } }}
          >
            {/* 呼び出しセクション */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.05em' }}>
                呼び出し
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                コースの並び順・車両構成を土台として読み込み
              </Typography>
            </Box>
            {isLoadingFixedRoutes ? (
              <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
                読み込み中...
              </Box>
            ) : fixedRoutes.length === 0 ? (
              <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
                登録済みの固定コースはありません
              </Box>
            ) : (
              fixedRoutes.map((route) => (
                <MenuItem
                  key={route.id}
                  onClick={() => handlePickFixedRoute(route)}
                  disabled={!startOn || isApplying}
                  sx={{
                    mx: 0.5,
                    borderRadius: 1,
                    py: 1,
                    '& .row-actions': { opacity: 0 },
                    '&:hover .row-actions': { opacity: 1 },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <DownloadIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {route.name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                        登録日: {formatRegistrationDate(route.updatedAt)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.25}
                      className="row-actions"
                      sx={{ flexShrink: 0, transition: 'opacity 0.15s' }}
                    >
                      <Tooltip title="名前変更">
                        <IconButton
                          size="small"
                          onClick={(e) => handleRenameClick(e, route)}
                          sx={{ width: 24, height: 24 }}
                        >
                          <EditIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, route)}
                          sx={{ width: 24, height: 24, '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </MenuItem>
              ))
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* 保存するセクション */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.05em' }}>
                保存する
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                現在の配車計画を固定コースとして保存
              </Typography>
            </Box>
            <MenuItem
              onClick={handleOpenSaveAsNew}
              sx={{ mx: 0.5, borderRadius: 1, gap: 1 }}
            >
              <SaveOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2">新規保存</Typography>
            </MenuItem>
            <MenuItem
              onClick={handleOpenOverwriteSave}
              disabled={fixedRouteId == null}
              sx={{ mx: 0.5, borderRadius: 1, gap: 1, mb: 0.5 }}
            >
              <SaveOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2">上書き保存</Typography>
            </MenuItem>
          </Popover>

          {/* 自動配車: 車両と案件の両方が選択されているときに有効 */}
          <Button
            size="small"
            startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
            onClick={onAutoDispatch}
            disabled={isAutoDispatchDisabled}
            title={autoDispatchTooltip}
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
          >
            自動配車
          </Button>

          {/* インポート */}
          <Button
            size="small"
            startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
            onClick={() => { onNavigate('/import'); }}
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
          >
            インポート
          </Button>

          {/* + 案件作成（V3Plans と同じ ShipmentOrderForm） */}
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setNewShipmentDialogOpen(true)}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            案件作成
          </Button>

          {/* 表示設定の区切り */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ ml: 1, pl: 1.5, borderLeft: 1, borderColor: 'divider' }}
          >
            <DisplayMetricToggle value={displayMetrics} onChange={onDisplayMetricsChange} />
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="caption"
                sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                表示列数
              </Typography>
              <InputBase
                type="number"
                inputProps={{ min: 2, max: 30 }}
                value={visibleColumns}
                onChange={(e) =>
                  onVisibleColumnsChange(Math.max(2, Math.min(30, Number(e.target.value) || 2)))
                }
                sx={{
                  width: 48,
                  height: 28,
                  px: 0.75,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontSize: 12,
                  '& input': { textAlign: 'center', p: 0 },
                }}
              />
            </Stack>
          </Stack>
        </Stack>

        {/* ---- ダイアログ群 ---- */}

        {/* 案件作成フォーム（V3Plans と同じ） */}
        {startOn && newShipmentDialogOpen && (
          <ShipmentOrderForm
            baseDate={new Date(startOn)}
            startOn={startOn}
            newShipmentDialogOpen={newShipmentDialogOpen}
            setNewShipmentDialogOpen={setNewShipmentDialogOpen}
          />
        )}

        <FixedRouteSelectorDialog
          open={selectorDialogOpen}
          fixedRoutes={fixedRoutes}
          isLoading={isLoadingFixedRoutes}
          onSelect={handleRouteSelect}
          onClose={() => onSelectorDialogOpenChange(false)}
        />

        <FixedRouteSaveDialog
          open={fixedRouteSaveDialogOpen}
          onClose={() => setFixedRouteSaveDialogOpen(false)}
          planningDate={startOn}
          currentFixedRouteId={fixedRouteId}
          onFixedRouteCreated={onFixedRouteCreated}
          mode={saveDialogMode}
        />

        {/* 固定ルート呼び出し確認ダイアログ */}
        <Dialog
          open={loadDialogOpen}
          onClose={() => {
            setLoadDialogOpen(false);
            setLoadTarget(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>固定ルートの呼び出し</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <DialogContentText>
                選択した固定コースの車両構成・並び順を土台として読み込みます。出荷データとの紐づけは行いません。
              </DialogContentText>
              {loadTarget && (
                <Box sx={{ borderRadius: 1, bgcolor: 'action.hover', px: 1.5, py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{loadTarget.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    登録日: {formatRegistrationDate(loadTarget.updatedAt)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  borderRadius: 1,
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography variant="caption">
                  ⚠ 既存の配車計画を1から組み換えることになります。現在の出荷データはすべて未割り当てに戻されます。
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                ※ 後から「固定ルートに対応づけ」で出荷データを紐づけることができます。
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setLoadDialogOpen(false);
                setLoadTarget(null);
              }}
            >
              キャンセル
            </Button>
            <Button variant="contained" onClick={handleLoadConfirm}>読み込み</Button>
          </DialogActions>
        </Dialog>

        {/* 名前変更ダイアログ */}
        <Dialog
          open={renameDialogOpen}
          onClose={() => setRenameDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>固定コース名の変更</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>新しい名前を入力してください。</DialogContentText>
            <TextField
              fullWidth
              size="small"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="コース名"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameConfirm();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenameDialogOpen(false)}>キャンセル</Button>
            <Button
              variant="contained"
              onClick={handleRenameConfirm}
              disabled={!renameValue.trim() || renameMutation.isPending}
            >
              変更
            </Button>
          </DialogActions>
        </Dialog>

        {/* 削除ダイアログ */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>固定コースの削除</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <DialogContentText>
                この固定コースを削除しますか？この操作は元に戻せません。
              </DialogContentText>
              {deleteTarget && (
                <Box
                  sx={{
                    borderRadius: 1,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {deleteTarget.name}
                  </Typography>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              削除
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

export default ConvenienceDispatchToolbar;
